import { useState, useEffect } from "react";
import {
  generatePhotoMappingData,
  generateTopViewMappingData,
  generateSideViewMappingData,
} from "./photoMapping";

function setColourForAllTriangles(shapeNetColours, colour) {
  // for all 8 triangles in that section
  for (let j = 0; j < 8; j++) {
    setTriangleColourInAllSections(shapeNetColours, j + 1, colour);
  }
}

function setAllTrianglesInASection(shapeNetColours, sectionNum, colour) {
  for (let j = 0; j < 8; j++) {
    setTriangleColour(shapeNetColours, sectionNum, j + 1, colour);
  }
}

function setTriangleColourInAllSections(
  shapeNetColours,
  triangleNumber,
  colour,
) {
  for (let i = 0; i < 5; i++) {
    setTriangleColour(shapeNetColours, i + 1, triangleNumber, colour);
  }
}

// sets matching triangle colour in shape net, side view and top view
function setTriangleColour(shapeNetColours, sectionNum, triangleNum, colour) {
  shapeNetColours[`s${sectionNum}t${triangleNum}`] = colour;
  shapeNetColours[`top-s${sectionNum}t${triangleNum}`] = colour;
  shapeNetColours[`side-s${sectionNum}t${triangleNum}`] = colour;
}

// function

export const Template = ({ colours, patternType, photoDataUrl }) => {
  const shapeNetColours = {};
  // default
  setColourForAllTriangles(shapeNetColours, "white");

  if (patternType === "singleColour") {
    setColourForAllTriangles(shapeNetColours, colours.c1);
  }

  if (patternType === "pentagons") {
    setColourForAllTriangles(shapeNetColours, colours.c1);
    setTriangleColourInAllSections(shapeNetColours, 2, colours.c2);
    setTriangleColourInAllSections(shapeNetColours, 6, colours.c2);
  }

  if (patternType === "hexagons") {
    setColourForAllTriangles(shapeNetColours, colours.c2);
    // The non-hexagon triangles
    setTriangleColourInAllSections(shapeNetColours, 1, colours.c1);
    setTriangleColourInAllSections(shapeNetColours, 8, colours.c1);
  }

  if (patternType === "sections") {
    setAllTrianglesInASection(shapeNetColours, 1, colours.c1);
    setAllTrianglesInASection(shapeNetColours, 2, colours.c2);
    setAllTrianglesInASection(shapeNetColours, 3, colours.c3);
    setAllTrianglesInASection(shapeNetColours, 4, colours.c4);
    setAllTrianglesInASection(shapeNetColours, 5, colours.c5);
  }

  if (patternType === "photo") {
    setColourForAllTriangles(shapeNetColours, "transparent");
  }

  // Track image natural dimensions for photo mapping
  const [imgDimensions, setImgDimensions] = useState(null);
  useEffect(() => {
    if (patternType === "photo" && photoDataUrl) {
      const img = new window.Image();
      img.onload = () => {
        setImgDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.src = photoDataUrl;
    } else {
      setImgDimensions(null);
    }
  }, [patternType, photoDataUrl]);

  const isPhotoMode = patternType === "photo" && photoDataUrl && imgDimensions;
  const shapeNetMappings = isPhotoMode
    ? generatePhotoMappingData(imgDimensions.width, imgDimensions.height)
    : [];
  const topViewMappings = isPhotoMode
    ? generateTopViewMappingData(imgDimensions.width, imgDimensions.height)
    : [];
  const sideViewMappings = isPhotoMode
    ? generateSideViewMappingData(imgDimensions.width, imgDimensions.height)
    : [];

  return (
    <svg
      className="template-page"
      viewBox="0 0 210 297"
      width="100%"
      xmlSpace="preserve"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        id="a4-sheet-bg"
        stroke="none"
        fill={"rgb(255,255,255)"}
        d="M0 0H210V297H0z"
      ></path>

      {/* SHAPE NET */}
      <g
        id="SHAPE_NET"
        style={{ transform: `scale(0.97) translate(3px, 5px)` }}
      >
        {/* Photo mapped onto shape net triangles (rendered first to be behind paths) */}
        {isPhotoMode && (
          <g id="photo-shape-net">
            <defs>
              {shapeNetMappings.map((m) => (
                <clipPath key={`clip-sn-${m.id}`} id={`clip-sn-${m.id}`}>
                  <polygon
                    points={m.clipVertices
                      .map((v) => `${v[0]},${v[1]}`)
                      .join(" ")}
                  />
                </clipPath>
              ))}
            </defs>
            {shapeNetMappings.map((m) => {
              const [a, b, c, d, e, f] = m.transform;
              return (
                <g key={`group-sn-${m.id}`} clipPath={`url(#clip-sn-${m.id})`}>
                  <image
                    href={photoDataUrl}
                    x={m.imgPlacement.x}
                    y={m.imgPlacement.y}
                    width={m.imgPlacement.width}
                    height={m.imgPlacement.height}
                    preserveAspectRatio="none"
                    transform={`matrix(${a},${b},${c},${d},${e},${f})`}
                  />
                </g>
              );
            })}
          </g>
        )}
        <g
          id="flaps"
          style={{ fill: "#dbdee3" }}
          stroke="none"
          transform="matrix(1.02538 .22435 -.22434 1.02538 32.01 -19.172)"
        >
          <path
            id="path222"
            style={{ fill: shapeNetColours["path222"] }}
            strokeOpacity="0.58"
            d="M72.575 229.633l11.074-22.791-22.794 24.279z"
          />
          <path
            id="path224"
            style={{ fill: shapeNetColours["path224"] }}
            strokeOpacity="0.58"
            d="M106.653 121.01l.263 25.337 9.56-31.9z"
          />
          <path
            id="path226"
            style={{ fill: shapeNetColours["path226"] }}
            strokeOpacity="0.675"
            d="M122.496 119.115l20.298 15.74-18.832-27.467z"
          />
          <path
            id="path228"
            style={{ fill: shapeNetColours["path228"] }}
            strokeOpacity="0.545"
            d="M128.517 74.348l-9.321 6.845 4.753 26.141z"
          />
          <path
            id="path230"
            style={{ fill: shapeNetColours["path230"] }}
            strokeOpacity="0.58"
            d="M44.725 169.716l25.253 2.08-30.88-12.468z"
          />
          <path
            id="path232"
            style={{ fill: shapeNetColours["path232"] }}
            strokeOpacity="0.675"
            d="M44.303 153.766l17.548-18.757-29.09 16.213z"
          />
          <path
            id="path234"
            style={{ fill: shapeNetColours["path234"] }}
            strokeOpacity="0.545"
            d="M.284 143.631l5.954 9.915 26.469-2.317z"
          />
          <path
            id="path236"
            style={{ fill: shapeNetColours["path236"] }}
            strokeOpacity="0.675"
            d="M57.552 224.256l-11.138-23.144 4.592 32.984z"
          />
          <path
            id="path238"
            style={{ fill: shapeNetColours["path238"] }}
            strokeOpacity="0.545"
            d="M32.181 261.629l11.397-1.965 7.415-25.514z"
          />
          <path
            id="path240"
            style={{ fill: shapeNetColours["path240"] }}
            strokeOpacity="0.58"
            d="M138.435 225.399l-17.212-18.596 14.342 30.055z"
          />
          <path
            id="path242"
            style={{ fill: shapeNetColours["path242"] }}
            strokeOpacity="0.675"
            d="M127.97 237.443l-25.61 1.972 32.4 7.701z"
          />
          <path
            id="path244"
            style={{ fill: shapeNetColours["path244"] }}
            strokeOpacity="0.545"
            d="M153.575 274.655l2.309-11.332-21.079-16.175z"
          />
          <path
            id="path246"
            style={{ fill: shapeNetColours["path246"] }}
            strokeOpacity="0.58"
            d="M158.352 162.304l-23.544 9.365 33.203-2.562z"
          />
          <path
            id="path248"
            style={{ fill: shapeNetColours["path248"] }}
            strokeOpacity="0.675"
            d="M165.822 176.404l-7.38 24.602 18.849-27.455z"
          />
          <path
            id="path250"
            style={{ fill: shapeNetColours["path250"] }}
            strokeOpacity="0.545"
            d="M209.757 165.91l-9.742-6.233-22.679 13.843z"
          />
          <path
            id="path252"
            style={{ fill: shapeNetColours["path252"] }}
            strokeOpacity="1"
            d="M106.9 146.355l-8.733 2.918 4.226 30.077z"
          />
        </g>

        <g id="s1" style={{ stroke: "#000", strokeWidth: "0.265" }}>
          <path
            id="s1t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s1t1"] }}
            d="M108.79 154.88l22.928 32.217-34.952.605z"
          >
            <title>s1t1</title>
          </path>
          <path
            id="s1t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s1t2"] }}
            d="M108.788 154.878l39.387-3.738-16.457 35.957z"
          >
            <title>s1t2</title>
          </path>
          <path
            id="s1t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s1t3"] }}
            d="M148.175 151.14l-39.367 3.736 16.96-30.565z"
          >
            <title>s1t3</title>
          </path>
          <path
            id="s1t4"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s1t4"] }}
            d="M131.719 187.094l16.456-35.954 17.985 29.974z"
          >
            <title>s1t4</title>
          </path>
          <path
            id="s1t5"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s1t5"] }}
            d="M148.175 151.14l39.472 2.382-21.471 27.583z"
          >
            <title>s1t5</title>
          </path>
          <path
            id="s1t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s1t6"] }}
            d="M187.667 153.528l-39.492-2.388 21.812-32.984z"
          >
            <title>s1t6</title>
          </path>
          <path
            id="s1t7"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s1t7"] }}
            d="M169.98 118.154l-21.805 32.986-13.148-32.388z"
          >
            <title>s1t7</title>
          </path>
          <path
            id="s1t8"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s1t8"] }}
            d="M147.11 85.895l22.87 32.26-34.954.54z"
          >
            <title>s1t8</title>
          </path>
        </g>

        <g id="s2" style={{ stroke: "#000", strokeWidth: "0.265" }}>
          <path
            id="s2t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s2t1"] }}
            d="M65.226 172.703l34.198-19.852-2.629 34.859z"
          >
            <title>s2t1</title>
          </path>
          <path
            id="s2t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s2t2"] }}
            d="M65.223 172.705l-.08-39.565 34.281 19.712z"
          >
            <title>s2t2</title>
          </path>
          <path
            id="s2t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s2t3"] }}
            d="M65.143 133.14l.08 39.544-28.866-19.712z"
          >
            <title>s2t3</title>
          </path>
          <path
            id="s2t4"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s2t4"] }}
            d="M99.421 152.85l-34.278-19.71 31.508-15.137z"
          >
            <title>s2t4</title>
          </path>
          <path
            id="s2t5"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s2t5"] }}
            d="M65.143 133.14l6.02-39.083 25.48 23.93z"
          >
            <title>s2t5</title>
          </path>
          <path
            id="s2t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s2t6"] }}
            d="M71.172 94.038l-6.029 39.102-30.827-24.768z"
          >
            <title>s2t6</title>
          </path>
          <path
            id="s2t7"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s2t7"] }}
            d="M34.314 108.38l30.829 24.76-33.465 10.098z"
          >
            <title>s2t7</title>
          </path>
          <path
            id="s2t8"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s2t8"] }}
            d="M.079 128.169l34.235-19.789-2.694 34.853z"
          >
            <title>s2t8</title>
          </path>
        </g>

        <g id="s3" style={{ stroke: "#000", strokeWidth: "0.265" }}>
          <path
            id="s3t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s3t1"] }}
            d="M71.397 211.69l-6.137-39.063 31.547 15.06z"
          >
            <title>s3t1</title>
          </path>
          <path
            id="s3t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s3t2"] }}
            strokeWidth="0.265"
            d="M71.398 211.694l-36.914-14.237 30.776-24.83z"
          >
            <title>s3t2</title>
          </path>
          <path
            id="s3t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s3t3"] }}
            strokeWidth="0.265"
            d="M34.484 197.457l36.895 14.23-28.819 19.78z"
          >
            <title>s3t3</title>
          </path>
          <path
            id="s3t4"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s3t4"] }}
            strokeWidth="0.265"
            d="M65.258 172.63l-30.774 24.827-2.715-34.85z"
          >
            <title>s3t4</title>
          </path>
          <path
            id="s3t5"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s3t5"] }}
            strokeWidth="0.265"
            d="M34.484 197.457L.225 177.707l31.526-15.098z"
          >
            <title>s3t5</title>
          </path>
          <path
            id="s3t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s3t6"] }}
            strokeWidth="0.132"
            d="M.21 177.693l34.274 19.764-34.242 19.78z"
          >
            <title>s3t6</title>
          </path>
          <path
            id="s3t7"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s3t7"] }}
            strokeWidth="0.265"
            d="M.249 217.243l34.235-19.786-2.691 34.852z"
          >
            <title>s3t7</title>
          </path>
          <path
            id="s3t8"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s3t8"] }}
            strokeWidth="0.265"
            d="M6.314 256.317L.249 217.243l31.519 15.118z"
          >
            <title>s3t8</title>
          </path>
        </g>

        <g id="s4" style={{ stroke: "#000", strokeWidth: "0.265" }}>
          <path
            id="s4t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s4t1"] }}
            d="M109.926 220.06L71.3 211.59l25.49-23.922z"
          >
            <title>s4t1</title>
          </path>
          <path
            id="s4t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s4t3"] }}
            d="M71.302 211.593l11.955 37.69-33.456-10.127z"
          >
            <title>s4t3</title>
          </path>
          <path
            id="s4t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s4t2"] }}
            d="M109.929 220.061l-26.672 29.223-11.956-37.694z"
          >
            <title>s4t2</title>
          </path>
          <path
            id="s4t4"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s4t4"] }}
            d="M83.257 249.284l26.658-29.208 7.963 34.036z"
          >
            <title>s4t4</title>
          </path>
          <path
            id="s4t5"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s4t5"] }}
            d="M83.257 249.284L52.413 274.03l-2.617-34.857z"
          >
            <title>s4t5</title>
          </path>
          <path
            id="s4t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s4t6"] }}
            d="M52.394 274.039l30.863-24.755 5.994 39.087z"
          >
            <title>s4t6</title>
          </path>
          <path
            id="s4t7"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s4t7"] }}
            d="M89.257 288.367l-6-39.083 31.494 15.165z"
          >
            <title>s4t7</title>
          </path>
          <path
            id="s4t8"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s4t8"] }}
            d="M127.866 296.908l-38.609-8.54 25.534-23.876z"
          >
            <title>s4t8</title>
          </path>
        </g>

        <g id="s5" style={{ stroke: "#000", strokeWidth: "0.265" }}>
          <path
            id="s5t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s5t1"] }}
            d="M131.716 187.082l-21.806 32.986-13.143-32.392z"
          >
            <title>s5t1</title>
          </path>
          <path
            id="s5t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s5t2"] }}
            d="M131.718 187.08l17.662 35.402-39.47-2.414z"
          >
            <title>s5t2</title>
          </path>
          <path
            id="s5t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s5t3"] }}
            d="M149.38 222.482l-17.653-35.384 34.62 4.822z"
          >
            <title>s5t3</title>
          </path>
          <path
            id="s5t4"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s5t4"] }}
            d="M109.913 220.069l39.467 2.413-21.492 27.568z"
          >
            <title>s5t4</title>
          </path>
          <path
            id="s5t5"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s5t5"] }}
            d="M149.38 222.482l11.984 37.684-33.462-10.105z"
          >
            <title>s5t5</title>
          </path>
          <path
            id="s5t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s5t6"] }}
            d="M161.366 260.187l-11.986-37.705 38.625 8.48z"
          >
            <title>s5t6</title>
          </path>
          <path
            id="s5t7"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s5t7"] }}
            d="M188.003 230.953l-38.623-8.47 25.486-23.924z"
          >
            <title>s5t7</title>
          </path>
          <path
            id="s5t8"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["s5t8"] }}
            d="M209.87 198.007l-21.867 32.946-13.083-32.416z"
          >
            <title>s5t8</title>
          </path>
        </g>
      </g>

      {/* TOP VIEW */}
      <g
        id="topView"
        style={{
          fill: shapeNetColours["topView"],
          stroke: "#000",
          strokeWidth: "0.265",
        }}
        transform="rotate(-.99 -129.653 62.084)"
      >
        {/* Photo mapped onto top view triangles (behind paths) */}
        {isPhotoMode && (
          <g id="photo-top-view">
            <defs>
              {topViewMappings.map((m) => (
                <clipPath key={`clip-tv-${m.id}`} id={`clip-tv-${m.id}`}>
                  <polygon
                    points={m.clipVertices
                      .map((v) => `${v[0]},${v[1]}`)
                      .join(" ")}
                  />
                </clipPath>
              ))}
            </defs>
            {topViewMappings.map((m) => (
              <g key={`group-tv-${m.id}`} clipPath={`url(#clip-tv-${m.id})`}>
                <image
                  href={photoDataUrl}
                  x={m.imgPlacement.x}
                  y={m.imgPlacement.y}
                  width={m.imgPlacement.width}
                  height={m.imgPlacement.height}
                  preserveAspectRatio="none"
                />
              </g>
            ))}
          </g>
        )}
        <g id="top-s2" style={{ stroke: "#000", fill: shapeNetColours.s2 }}>
          <path
            id="top-s2t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s2t1"] }}
            d="M54.137 63.368L38.79 58.32l15.36-11.237z"
          >
            <title>top-s2t1</title>
          </path>
          <path
            id="top-s2t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s2t2"] }}
            d="M38.748 42.212l15.432 4.86-15.358 11.202z"
          >
            <title>top-s2t2</title>
          </path>
          <path
            id="top-s2t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s2t3"] }}
            d="M38.748 42.212l-10.74 12.496 10.814 3.566z"
          >
            <title>top-s2t3</title>
          </path>
          <path
            id="top-s2t4"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s2t4"] }}
            d="M38.748 42.212l15.432 4.86.03-11.353z"
          >
            <title>top-s2t4</title>
          </path>
          <path
            id="top-s2t5"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s2t5"] }}
            d="M44.519 34.227l-5.728 8.067 15.478-6.575z"
          >
            <title>top-s2t5</title>
          </path>
          <path
            id="top-s2t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s2t6"] }}
            d="M44.519 34.227l-5.728 8.067-9.82 3.1z"
          >
            <title>top-s2t6</title>
          </path>
          <path
            id="top-s2t7"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s2t7"] }}
            d="M28.082 54.684l10.71-12.39-9.82 3.1z"
          >
            <title>top-s2t7</title>
          </path>
          <path
            id="top-s2t8"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s2t8"] }}
            d="M28.082 54.684l-4.795 8.707 5.684-17.997z"
          >
            <title>top-s2t8</title>
          </path>
        </g>

        <g id="top-s1" style={{ stroke: "#000", fill: shapeNetColours.s1 }}>
          <path
            id="top-s1t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s1t1"] }}
            d="M54.137 63.368l15.372-4.989-15.357-11.295z"
          >
            <title>top-s1t1</title>
          </path>
          <path
            id="top-s1t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s1t2"] }}
            d="M69.583 42.095L54.18 47.073l15.36 11.259z"
          >
            <title>top-s1t2</title>
          </path>
          <path
            id="top-s1t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s1t3"] }}
            d="M69.583 42.095L54.18 47.073l.03-11.354z"
          >
            <title>top-s1t3</title>
          </path>
          <path
            id="top-s1t4"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s1t4"] }}
            d="M69.583 42.095l10.768 12.67-10.81 3.567z"
          >
            <title>top-s1t4</title>
          </path>
          <path
            id="top-s1t5"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s1t5"] }}
            d="M69.583 42.095l10.768 12.67-1.193-9.546z"
          >
            <title>top-s1t5</title>
          </path>
          <path
            id="top-s1t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s1t6"] }}
            d="M69.583 42.095l-5.844-8.077 15.419 11.2z"
          >
            <title>top-s1t6</title>
          </path>
          <path
            id="top-s1t7"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s1t7"] }}
            d="M69.583 42.095l-5.844-8.077-9.47 1.7z"
          >
            <title>top-s1t7</title>
          </path>
          <path
            id="top-s1t8"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s1t8"] }}
            d="M44.519 34.227l19.22-.21-9.47 1.702z"
          >
            <title>top-s1t8</title>
          </path>
        </g>

        <g id="top-s5" style={{ stroke: "#000", fill: shapeNetColours.s5 }}>
          <path
            id="top-s5t8"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s5t8"] }}
            d="M85.03 63.368l-5.786-17.985 1.08 9.277z"
          >
            <title>top-s5t8</title>
          </path>
          <path
            id="top-s5t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s5t3"] }}
            d="M70.34 85.63l8.729-14.254-15.358 5.14z"
          >
            <title>top-s5t3</title>
          </path>
          <path
            id="top-s5t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s5t2"] }}
            d="M69.525 58.414l9.544 12.962-15.358 5.14z"
          >
            <title>top-s5t2</title>
          </path>
          <path
            id="top-s5t4"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s5t4"] }}
            d="M69.525 58.414l9.544 12.962 1.254-16.716z"
          >
            <title>top-s5t4</title>
          </path>
          <path
            id="top-s5t7"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s5t7"] }}
            d="M85.03 63.368l-5.961 8.008 1.254-16.716z"
          >
            <title>top-s5t7</title>
          </path>
          <path
            id="top-s5t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s5t6"] }}
            d="M85.03 63.368l-5.961 8.008.03 10.035z"
          >
            <title>top-s5t6</title>
          </path>
          <path
            id="top-s5t5"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s5t5"] }}
            d="M70.34 85.63l8.729-14.254-.086 10.268z"
          >
            <title>top-s5t5</title>
          </path>
          <path
            id="top-s5t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s5t1"] }}
            d="M54.137 63.368l15.372-4.989-5.856 18.078z"
          >
            <title>top-s5t1</title>
          </path>
        </g>

        <g id="top-s4" style={{ stroke: "#000", fill: shapeNetColours.s4 }}>
          <path
            id="top-s4t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s4t2"] }}
            d="M54.137 89.477l-9.517-13.03 19.033.01z"
          >
            <title>top-s4t2</title>
          </path>
          <path
            id="top-s4t8"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s4t8"] }}
            d="M70.34 85.63l-6.776 7.018 15.419-11.004z"
          >
            <title>top-s4t8</title>
          </path>
          <path
            id="top-s4t4"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s4t4"] }}
            d="M54.137 89.477l-9.517-13.03-6.672 9.102z"
          >
            <title>top-s4t4</title>
          </path>
          <path
            id="top-s4t5"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s4t5"] }}
            d="M44.694 92.741l9.544-3.24-16.29-3.952z"
          >
            <title>top-s4t5</title>
          </path>
          <path
            id="top-s4t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s4t6"] }}
            d="M44.694 92.741l9.544-3.24 9.24 3.1z"
          >
            <title>top-s4t6</title>
          </path>
          <path
            id="top-s4t7"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s4t7"] }}
            d="M70.34 85.63l-16.102 3.871 9.24 3.1z"
          >
            <title>top-s4t7</title>
          </path>
          <path
            id="top-s4t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s4t3"] }}
            d="M70.34 85.63l-16.102 3.871 9.473-12.986z"
          >
            <title>top-s4t3</title>
          </path>
          <path
            id="top-s4t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s4t1"] }}
            d="M54.137 63.368L44.62 76.446l19.033.011z"
          >
            <title>top-s4t1</title>
          </path>
        </g>

        <g id="top-s3" style={{ stroke: "#000", fill: shapeNetColours.s3 }}>
          <path
            id="top-s3t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s3t1"] }}
            d="M54.137 63.368L44.62 76.446l-5.798-18.172z"
          >
            <title>top-s3t1</title>
          </path>
          <path
            id="top-s3t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s3t2"] }}
            d="M29.073 71.527l15.547 4.92-5.798-18.173z"
          >
            <title>top-s3t2</title>
          </path>
          <path
            id="top-s3t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s3t3"] }}
            d="M29.13 71.644l15.49 4.802-6.672 9.103z"
          >
            <title>top-s3t3</title>
          </path>
          <path
            id="top-s3t4"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s3t4"] }}
            d="M29.073 71.527l-1.065-16.82 10.814 3.567z"
          >
            <title>top-s3t4</title>
          </path>
          <path
            id="top-s3t5"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s3t5"] }}
            d="M29.073 71.527l-1.065-16.82-4.807 8.637z"
          >
            <title>top-s3t5</title>
          </path>
          <path
            id="top-s3t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s3t6"] }}
            d="M29.073 71.527l.042 9.873-5.914-18.056z"
          >
            <title>top-s3t6</title>
          </path>
          <path
            id="top-s3t7"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s3t7"] }}
            d="M29.13 71.644l-.073 9.698 8.89 4.207z"
          >
            <title>top-s3t7</title>
          </path>
          <path
            id="top-s3t8"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["top-s3t8"] }}
            d="M44.694 92.741l-15.637-11.4 8.89 4.208z"
          >
            <title>top-s3t8</title>
          </path>
        </g>
      </g>

      {/* SIDE VIEW */}
      <g
        id="side-view"
        style={{ stroke: "#000", strokeWidth: "0.265" }}
        transform="matrix(.65028 0 0 .65013 43.738 -81.979)"
      >
        {/* Photo mapped onto side view triangles (behind paths) */}
        {isPhotoMode && (
          <g id="photo-side-view">
            <defs>
              {sideViewMappings.map((m) => (
                <clipPath key={`clip-side-${m.id}`} id={`clip-side-${m.id}`}>
                  <polygon
                    points={m.clipVertices
                      .map((v) => `${v[0]},${v[1]}`)
                      .join(" ")}
                  />
                </clipPath>
              ))}
            </defs>
            {sideViewMappings.map((m) => {
              const [a, b, c, d, e, f] = m.transform;
              return (
                <g
                  key={`group-side-${m.id}`}
                  clipPath={`url(#clip-side-${m.id})`}
                >
                  <image
                    href={photoDataUrl}
                    x={m.imgPlacement.x}
                    y={m.imgPlacement.y}
                    width={m.imgPlacement.width}
                    height={m.imgPlacement.height}
                    preserveAspectRatio="none"
                    transform={`matrix(${a},${b},${c},${d},${e},${f})`}
                  />
                </g>
              );
            })}
          </g>
        )}
        <g id="side-s3" style={{ stroke: "#000", fill: shapeNetColours.s3 }}>
          <path
            id="side-s3t7"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s3t7"] }}
            d="M117.866 217.281l-13.768-7.236.827 31.98z"
          >
            <title>side-s3t7</title>
          </path>
          <path
            id="side-s3t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s3t3"] }}
            d="M117.866 217.281l-13.768-7.236 25.997-18.044z"
          >
            <title>side-s3t3</title>
          </path>
          <path
            id="side-s3t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s3t2"] }}
            d="M122.428 189.437l-18.33 20.608 25.997-18.044z"
          >
            <title>side-s3t2</title>
          </path>
          <path
            id="side-s3t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s3t1"] }}
            d="M122.428 189.437l26.188-9.595L130.095 192z"
          >
            <title>side-s3t1</title>
          </path>
          <path
            id="side-s3t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s3t6"] }}
            d="M96.786 235.686l7.312-25.641.827 31.98z"
          >
            <title>side-s3t6</title>
          </path>
          <path
            id="side-s3t8"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s3t8"] }}
            d="M104.925 242.025l12.94-24.744 12.818 28.945z"
          >
            <title>side-s3t8</title>
          </path>
        </g>

        <g id="side-s5" style={{ stroke: "#000", fill: shapeNetColours.s5 }}>
          <path
            id="side-s5t5"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s5t5"] }}
            d="M192.595 209.73l-.726 32.406-12.458-24.809z"
          >
            <title>side-s5t5</title>
          </path>
          <path
            id="side-s5t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s5t6"] }}
            d="M192.595 209.73l-.726 32.406 7.992-6.718z"
          >
            <title>side-s5t6</title>
          </path>
          <path
            id="side-s5t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s5t2"] }}
            d="M174.976 189.437l17.68 20.45-25.83-17.572z"
          >
            <title>side-s5t2</title>
          </path>
          <path
            id="side-s5t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s5t1"] }}
            d="M174.976 189.437l-26.367-9.595 18.218 12.473z"
          >
            <title>side-s5t1</title>
          </path>
          <path
            id="side-s5t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s5t3"] }}
            d="M192.556 210.073l-13.114 7.366-12.615-25.124z"
          >
            <title>side-s5t3</title>
          </path>
        </g>

        <g id="side-s4" style={{ stroke: "#000", fill: shapeNetColours.s4 }}>
          <path
            id="side-s4t2"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s4t2"] }}
            d="M166.827 192.315L130.095 192l18.405 22.524z"
          >
            <title>side-s4t2</title>
          </path>
          <path
            id="side-s4t8"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s4t8"] }}
            d="M191.87 242.136l-12.459-24.809-12.93 28.9z"
          >
            <title>side-s4t8</title>
          </path>
          <path
            id="side-s4t3"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s4t3"] }}
            d="M148.548 214.45l30.894 2.989-12.615-25.124z"
          >
            <title>side-s4t3</title>
          </path>
          <path
            id="side-s4t5"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s4t5"] }}
            d="M117.866 217.281l12.817 28.945 17.817-31.888z"
          >
            <title>side-s4t5</title>
          </path>
          <path
            id="side-s4t6"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s4t6"] }}
            d="M166.317 246.226h-35.634l17.817-31.888z"
          >
            <title>side-s4t6</title>
          </path>
          <path
            id="side-s4t7"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s4t7"] }}
            d="M166.317 246.226l13.236-29.177-31.053-2.71z"
          >
            <title>side-s4t7</title>
          </path>
          <path
            id="side-s4t1"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s4t1"] }}
            d="M166.827 192.315L130.095 192l18.521-12.158z"
          >
            <title>side-s4t1</title>
          </path>
          <path
            id="side-s4t4"
            className="triangle-hoverable"
            style={{ fill: shapeNetColours["side-s4t4"] }}
            d="M117.866 217.281l12.229-25.28 18.405 22.523z"
          >
            <title>side-s4t4</title>
          </path>
        </g>
      </g>

      {/* TITLES & TEXT */}

      <text
        id="title"
        x="104.865"
        y="16.858"
        style={{ fill: "#000" }}
        fontFamily="Calibri"
        fontSize="8.467"
      >
        <tspan
          style={{ WebkitTextAlign: "center", textAlign: "center" }}
          x="104.865"
          y="16.858"
          textAnchor="middle"
        >
          ArtFly Geodesic Dome Maker
        </tspan>
      </text>

      <text
        id="topLabel"
        x="23.347"
        y="36.633"
        style={{ fill: "#000" }}
        fillOpacity="0.58"
        fontFamily="Calibri"
        fontSize="5.644"
      >
        <tspan
          style={{ WebkitTextAlign: "center", textAlign: "center" }}
          x="23.347"
          y="36.633"
          textAnchor="middle"
        >
          Top:
        </tspan>
      </text>

      <text
        id="sideLabel"
        x="113.305"
        y="38.75"
        style={{ fill: "#000" }}
        fillOpacity="0.58"
        fontFamily="Calibri"
        fontSize="5.644"
      >
        <tspan
          style={{ WebkitTextAlign: "center", textAlign: "center" }}
          x="113.305"
          y="38.75"
          textAnchor="middle"
        >
          Side:
        </tspan>
      </text>

      <text
        xmlSpace="preserve"
        style={{
          lineHeight: "120%",
          InkscapeFontSpecification: "'Calibri, Normal'",
          WebkitTextAlign: "start",
          textAlign: "start",
          fill: "#000",
        }}
        id="shapeNetLabel"
        x="12.234"
        y="101.191"
        fillOpacity="0.58"
        fontFamily="Calibri"
        fontSize="5.644"
        textAnchor="start"
        writingMode="lr-tb"
      >
        <tspan
          style={{ WebkitTextAlign: "start", textAlign: "start" }}
          id="tspan5261"
          x="12.234"
          y="101.191"
          textAnchor="start"
        >
          Shape Net:
        </tspan>
      </text>

      <text
        xmlSpace="preserve"
        style={{ lineHeight: "120%" }}
        id="text5856"
        x="102.722"
        y="23.404"
        fillOpacity="0.58"
        fontFamily="Calibri"
        fontSize="5.644"
        writingMode="lr-tb"
      >
        <tspan
          style={{ WebkitTextAlign: "center", textAlign: "center" }}
          id="tspan5854"
          x="102.722"
          y="23.404"
          textAnchor="middle"
        >
          Website: dome.artfly.io
        </tspan>
      </text>

      <g
        id="logo"
        style={{ fill: shapeNetColours["g6015"] }}
        transform="matrix(.06497 0 0 .06497 43.19 9.08)"
      >
        <g style={{ fill: "#fff", stroke: "#000", strokeWidth: "3" }}>
          <path d="M95.84 147c7.464-.428 15.57-9.89 9.66-20.65-5.727-10.44-14.36-19.27-14.36-19.27-14.85 10.37-7.205 40.93 4.702 39.92z" />
          <path d="M145.9 112.8c-5.492 12.88-22.3 21.91-35.24 13.49-12.55-8.165-21.31-20.44-21.31-20.44 22.67-20.91 65.34-19.59 56.55 6.943zM1.523 52.73c.44-7.479 9.914-15.58 20.67-9.68 10.43 5.72 19.27 14.36 19.27 14.36-10.37 14.85-40.92 7.204-39.94-4.682z" />
          <path d="M35.8 2.651c-12.88 5.492-21.91 22.3-13.49 35.24 8.164 12.55 20.44 21.31 20.44 21.31 20.91-22.66 19.59-65.34-6.944-56.55z" />
          <path d="M123 75.55L73.76 26.32l53.12-4.082-1.891 25.63z" />
          <path d="M87.93 40.49c14.05 1.436 19.09 9.898 20.89 20.89l-61.79 61.79-20.89-20.89z" />
        </g>

        <g style={{ fill: "#000", stroke: "#000", strokeWidth: "3" }}>
          <path d="M106.2 23.82c7.53 4.243 14.17 10.23 19.24 19.24l1.534-20.77z" />
          <path d="M73.76 26.32c11.61-1.005 14.52 5.323 14.3 14.3l-61.79 61.79-15.82-12.88zm34.94 34.93c11.61-1.005 14.52 5.323 14.3 14.3l-63.371 63.018-12.72-15.529z" />
        </g>

        <g style={{ fill: "#fff", stroke: "#000", strokeWidth: "3" }}>
          <ellipse
            cx="84.549"
            cy="-4.478"
            rx="20.44"
            ry="20.7"
            transform="rotate(45)"
          />
        </g>

        <ellipse
          cx="84.549"
          cy="-4.478"
          rx="9.527"
          ry="9.646"
          transform="rotate(45)"
        />

        <ellipse
          cx="125.099"
          cy="-4.478"
          style={{ fill: "#fff", stroke: "#000", strokeWidth: "3" }}
          rx="20.44"
          ry="20.7"
          transform="rotate(45)"
        />
        <ellipse
          cx="125.099"
          cy="-4.478"
          rx="9.527"
          ry="9.646"
          transform="rotate(45)"
        />
        <path
          style={{ fill: "#fff", stroke: "#000", strokeWidth: "3" }}
          d="M13.54 111l-3.09-21.47 22.85 5.168 21.74 20.33 4.589 23.54-21.24-3.28z"
        />
        <ellipse
          cx="105.599"
          cy="56.01"
          style={{ stroke: "#000", strokeWidth: "3" }}
          rx="10.24"
          ry="2.497"
          transform="rotate(45)"
        />
        <ellipse
          cx="89.773"
          cy="-13.091"
          style={{ fill: "#fff", stroke: "#000", strokeWidth: "1" }}
          rx="6.116"
          ry="6.192"
          transform="scale(.95347 1.04446) rotate(45)"
        />
        <ellipse
          cx="130.904"
          cy="-15.001"
          style={{ fill: "#fff", stroke: "#000", strokeWidth: "1" }}
          rx="6.116"
          ry="6.192"
          transform="scale(.95347 1.04446) rotate(45)"
        />
      </g>
    </svg>
  );
};
